import React, { useState, useEffect, useCallback } from 'react'

interface PanStatus {
  transform: [number, number]
  clearTransform: () => void
}

export const usePanControls = (element: React.RefObject<HTMLDivElement>, paused: boolean): PanStatus => {
  const [previous, setPrevious] = useState(0)
  const [isPanning, setIsPanning] = useState(false)
  const [[ax, ay], setAnchor] = useState<[number, number]>([0, 0])
  const [[tx, ty], setTransform] = useState<[number, number]>([0, 0])

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      if (paused) {
        return
      }
      setPrevious(Date.now())
      setIsPanning(true)
      setAnchor([e.pageX, e.pageY])
    },
    [paused]
  )

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const t = Date.now()
      if (!isPanning || t - previous < 25) {
        return
      }

      const [ex, ey] = [e.pageX, e.pageY]

      const dx = ex - ax
      const dy = ey - ay

      setTransform([tx + dx, ty + dy])
      setAnchor([ex, ey])
    },
    [ax, ay, tx, ty, isPanning, previous]
  )

  const handlePointerUp = useCallback(() => {
    setIsPanning(false)
  }, [])

  useEffect(() => {
    const el = element.current

    if (!el) {
      return
    }

    el.addEventListener('pointerdown', handlePointerDown)
    el.addEventListener('pointermove', handlePointerMove)
    el.addEventListener('pointerup', handlePointerUp)

    return () => {
      el.removeEventListener('pointerdown', handlePointerDown)
      el.removeEventListener('pointermove', handlePointerMove)
      el.removeEventListener('pointerup', handlePointerUp)
    }
  }, [element, handlePointerDown, handlePointerMove, handlePointerUp])

  return { transform: [tx, ty], clearTransform: () => setTransform([0, 0]) }
}
